import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePilgrimGuideStore } from '../../stores/pilgrimGuideStore';
import { ErrorMessage } from '../common/ErrorMessage';
import { useLanguage } from '../../contexts/LanguageContext';
import { Loader2, ChevronRight, ChevronLeft, Search, Filter, Building } from 'lucide-react';
import { LoadingProgress } from '../common/LoadingProgress';
import { LinkingTableRow, LinkingTableRowData } from './LinkingTableRow';

const ITEMS_PER_PAGE = 50;
type LinkingViewFilter = 'all' | 'linked' | 'unlinked';
const matchesDashboardPilgrim = (row: LinkingTableRowData) =>
  Boolean(row.pilgrimId) && !row.pilgrimId.startsWith('waiting_');

export const LinkingTable = () => {
  const { 
    links, 
    error, 
    initialized,
    loading,
    progress,
    fetchLinks,
  } = usePilgrimGuideStore();
  const { translations } = useLanguage();
  const isAdmin = localStorage.getItem('userRole') === 'admin';

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedOrganizer, setSelectedOrganizer] = useState('');
  const [selectedLinkingView, setSelectedLinkingView] = useState<LinkingViewFilter>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const statusLabels: Record<string, string> = {
    approved: translations?.linkingTable?.filters?.approved || 'Approved',
    pending: translations?.linkingTable?.filters?.pending || 'Pending',
    disputed: translations?.linkingTable?.filters?.disputed || 'Disputed',
    rejected: translations?.linkingTable?.filters?.rejected || 'Rejected',
    waiting_identification:
      translations?.linkingTable?.filters?.waitingIdentification || 'Waiting Identification',
    unlinked: translations?.linkingTable?.filters?.unlinked || 'Not Linked',
  };

  // Initialize data
  useEffect(() => {
    if (!initialized) {
      fetchLinks();
    }
  }, [initialized, fetchLinks]);

  useEffect(() => {
    if (selectedLinkingView !== 'unlinked') return;

    if (selectedStatus) {
      setSelectedStatus('');
    }

    if (selectedOrganizer) {
      setSelectedOrganizer('');
    }
  }, [selectedLinkingView, selectedStatus, selectedOrganizer]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedStatus, selectedCountry, selectedOrganizer, selectedLinkingView]);

  const linkedRows = useMemo<LinkingTableRowData[]>(() => {
    return Object.values(links).map((link) => ({
      id: link.id,
      pilgrimId: link.pilgrimId,
      pilgrimName: link.pilgrimName,
      passportNumber: String(link.passportNumber || ''),
      gender: link.gender || 'unknown',
      residenceCountry: link.residenceCountry || null,
      guideName: link.guideName || null,
      organizerName: link.organizerName || null,
      status: link.disputeId ? 'disputed' : link.status,
      isLinked: true,
    }));
  }, [links]);

  const rows = linkedRows;

  // Get unique organizers
  const organizers = useMemo(() => {
    return Array.from(
      new Set(
        linkedRows
          .map((row) => row.organizerName)
          .filter((organizer): organizer is string => Boolean(organizer))
      )
    ).sort();
  }, [linkedRows]);

  // Get unique countries
  const countries = useMemo(() => {
    return Array.from(
      new Set(
        rows
          .map((row) => row.residenceCountry)
          .filter((country): country is string => Boolean(country))
      )
    ).sort();
  }, [rows]);

  // Memoized filtered rows
  const filteredRows = useMemo(() => {
    const statusOrder = (s: string) =>
      s === 'approved' ? 0 : s === 'pending' ? 1 : s === 'disputed' ? 2
      : s === 'waiting_identification' ? 3 : 4;

    return rows.filter((row) => {
      const matchesSearch = !searchTerm ||
        row.pilgrimName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.passportNumber.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesLinkingView =
        selectedLinkingView === 'all' ||
        (selectedLinkingView === 'linked'
          ? matchesDashboardPilgrim(row)
          : !matchesDashboardPilgrim(row));

      // "approved" filter includes pending — both mean "org has a real claim,
      // pilgrim exists." The only difference is guide assignment.
      const matchesStatus = !selectedStatus
        || (row.isLinked && row.status === selectedStatus)
        || (selectedStatus === 'approved' && row.isLinked && row.status === 'pending');

      const matchesCountry = !selectedCountry || row.residenceCountry === selectedCountry;
      const matchesOrganizer = !selectedOrganizer || row.organizerName === selectedOrganizer;

      return (
        matchesSearch &&
        matchesLinkingView &&
        matchesStatus &&
        matchesCountry &&
        matchesOrganizer
      );
    }).sort((a, b) => statusOrder(a.status) - statusOrder(b.status));
  }, [rows, searchTerm, selectedLinkingView, selectedStatus, selectedCountry, selectedOrganizer]);

  // Calculate pagination
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedRows = filteredRows.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  const isTableLoading = loading;

  if (error) {
    return <ErrorMessage message={error} onRetry={fetchLinks} />;
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-white rounded-lg shadow-sm"
      >
        {/* Loading Progress */}
        {(loading || !initialized) && progress.total > 0 && (
          <div className="p-4 border-b">
            <LoadingProgress
              progress={progress.current}
              total={progress.total}
              label={translations?.loading?.loadingLinks || 'Loading links...'}
            />
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow-sm space-y-4">
          {isAdmin && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-600">
                {translations?.linkingTable?.filters?.linkingView || 'Pilgrim View'}:
              </span>
              {([
                ['all', translations?.linkingTable?.filters?.allPilgrims || 'All'],
                ['linked', translations?.linkingTable?.filters?.linkedPilgrims || 'Linked'],
                ['unlinked', translations?.linkingTable?.filters?.unlinkedPilgrims || 'Not Linked'],
              ] as Array<[LinkingViewFilter, string]>).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setSelectedLinkingView(value)}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    selectedLinkingView === value
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <input
                type="text"
                placeholder={translations?.linkingTable?.filters?.searchPlaceholder || 'Search by name or passport...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            </div>

            {/* Status Filter */}
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              disabled={selectedLinkingView === 'unlinked'}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">
                {translations?.linkingTable?.filters?.allStatuses || 'All Statuses'}
              </option>
              <option value="approved">
                {statusLabels.approved}
              </option>
              <option value="pending">
                {statusLabels.pending}
              </option>
              <option value="disputed">
                {statusLabels.disputed}
              </option>
              <option value="rejected">
                {statusLabels.rejected}
              </option>
              <option value="waiting_identification">
                {statusLabels.waiting_identification}
              </option>
            </select>

            {/* Country Filter */}
            <select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">
                {translations?.linkingTable?.filters?.allCountries || 'All Countries'}
              </option>
              {countries.map((country) => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>

            {/* Organizer Filter */}
            <select
              value={selectedOrganizer}
              onChange={(e) => setSelectedOrganizer(e.target.value)}
              disabled={selectedLinkingView === 'unlinked'}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">
                {translations?.linkingTable?.filters?.allOrganizers || 'All Organizers'}
              </option>
              {organizers.map((organizer) => (
                <option key={organizer} value={organizer}>{organizer}</option>
              ))}
            </select>
          </div>

          {/* Active Filters */}
          {(searchTerm || selectedStatus || selectedCountry || selectedOrganizer || (isAdmin && selectedLinkingView !== 'all')) && (
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-primary-500" />
              <span className="text-sm text-gray-600">
                {translations?.linkingTable?.filters?.activeFilters || 'Active Filters:'}
              </span>
              <div className="flex flex-wrap gap-2">
                {isAdmin && selectedLinkingView !== 'all' && (
                  <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                    {translations?.linkingTable?.filters?.linkingView || 'Pilgrim View'}:{' '}
                    {selectedLinkingView === 'linked'
                      ? translations?.linkingTable?.filters?.linkedPilgrims || 'Linked'
                      : translations?.linkingTable?.filters?.unlinkedPilgrims || 'Not Linked'}
                  </span>
                )}
                {searchTerm && (
                  <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                    {translations?.linkingTable?.filters?.search || 'Search'}: {searchTerm}
                  </span>
                )}
                {selectedStatus && (
                  <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                    {translations?.linkingTable?.filters?.status || 'Status'}:{' '}
                    {statusLabels[selectedStatus] || selectedStatus}
                  </span>
                )}
                {selectedCountry && (
                  <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                    {translations?.linkingTable?.filters?.country || 'Country'}: {selectedCountry}
                  </span>
                )}
                {selectedOrganizer && (
                  <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded flex items-center gap-1">
                    <Building size={12} />
                    <span>{translations?.linkingTable?.filters?.organizer || 'Organizer'}: {selectedOrganizer}</span>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                  {translations?.linkingTable?.tableHeaders?.pilgrim || 'Pilgrim'}
                </th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                  {translations?.linkingTable?.tableHeaders?.guide || 'Guide'}
                </th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                  {translations?.linkingTable?.tableHeaders?.organizer || 'Organizer'}
                </th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                  {translations?.linkingTable?.tableHeaders?.status || 'Status'}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedRows.map((row) => (
                <LinkingTableRow key={row.id} row={row} />
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className={`p-1 rounded-lg ${
                currentPage === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ChevronRight size={20} />
            </button>

            <span className="text-sm text-gray-700">
              {translations?.pagination?.page || 'Page'} {currentPage}{' '}
              {translations?.pagination?.of || 'of'} {totalPages}
            </span>

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className={`p-1 rounded-lg ${
                currentPage === totalPages
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ChevronLeft size={20} />
            </button>
          </div>

          <div className="text-sm text-gray-500">
            {translations?.pagination?.showing || 'Showing'} {filteredRows.length === 0 ? 0 : startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, filteredRows.length)} {translations?.pagination?.of || 'of'} {filteredRows.length}
          </div>
        </div>

        {/* Bottom section with loading indicator or empty state */}
        <div className="p-4 border-t">
          <div className="flex items-center justify-between">
            {isTableLoading && (
              <div className="flex items-center gap-2 text-primary-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">
                  {translations?.loading?.loadingData || 'Loading data...'}
                </span>
              </div>
            )}
            <span className="text-sm text-gray-500">
              {translations?.linkingTable?.totalPilgrims || 'Total'}: {filteredRows.length} {translations?.pagination?.pilgrims || 'pilgrims'}
            </span>
          </div>
        </div>

        {filteredRows.length === 0 && !isTableLoading && initialized && (
          <div className="p-8 text-center text-gray-500">
            {selectedLinkingView === 'linked'
              ? translations?.linkingTable?.noLinkedPilgrims || 'No linked pilgrims match the selected filters.'
              : selectedLinkingView === 'unlinked'
                ? translations?.linkingTable?.noUnlinkedPilgrims || 'No non-linked pilgrims match the selected filters.'
                : translations?.linkingTable?.noResults || 'No pilgrims match the selected filters.'}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};
